import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "@tanstack/react-router";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { api_endpoint } from "@/constants/constant";
import { apiRequest } from "@/lib/apiRequest";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import { useSidebarPageTitle } from "@/shared/useSidebarPageTitle";
import { queryKeys } from "@/lib/queryKeys";
import { Badge } from "../../@/components/ui/badge";
import { Button } from "../../@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../@/components/ui/card";
import { Checkbox } from "../../@/components/ui/checkbox";
import { Separator } from "../../@/components/ui/separator";
import { formatDistanceToNow, add, format } from "date-fns";
import {
  ArrowUpRight,
  BookOpen,
  CalendarDays,
  Eye,
  MoreVertical,
  ShieldCheck,
  Users,
  Send,
  ChevronRight,
  ChartLine,
  MessageSquare,
  BookOpenIcon,
} from "lucide-react";
import { DiscordIcon } from "@/components/icons/DiscordIcon";
import MakeAnnouncementDialog from "../forms/makeAnnouncementDialog";
import DefaultTable from "../components/defaultTable";
import Pagination from "../components/pagination";
import { Spinner } from "../components/spinner";
import { EmptyState } from "../components/emptyState";
import UpcomingClassesSection from "../components/UpcomingClassesSection";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../@/components/ui/dialog";
import DashboardStatCard from "../components/DashboardStatCard";
import MarkAttendanceDialog from "../forms/markAttendance";
import PendingAttendanceLogsSection from "../components/pendingAttendanceSection";
import UnreviewedCard from "@/components/unreviewedCard";

const makeId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

const getSessionDateValue = (session = {}) =>
  session.sessionDate ||
  session.session_date ||
  session.date ||
  session.sessionDay ||
  session.created_at ||
  "";

const formatSessionDateLabel = (session) => {
  const value =
    typeof session === "string" ? session : getSessionDateValue(session);
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const parseAttendanceTotals = (value) => {
  if (!value || typeof value !== "string") {
    return { present: 0, total: 0 };
  }
  const [presentRaw, totalRaw] = value.split("/");
  return {
    present: Number(presentRaw) || 0,
    total: Number(totalRaw) || 0,
  };
};

const formatRelativeTime = (value) => {
  if (!value) return "Just now";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return formatDistanceToNow(date, { addSuffix: true });
};

const getAttendanceStatus = (session) => {
  const raw = getSessionDateValue(session);
  if (!raw) return "PENDING";
  const sessionDate = new Date(raw);
  if (Number.isNaN(sessionDate.getTime())) return "PENDING";
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const sessionStart = new Date(sessionDate);
  sessionStart.setHours(0, 0, 0, 0);
  const diff = sessionStart.getTime() - todayStart.getTime();
  if (diff === 0) return "LIVE";
  if (diff > 0) return "UPCOMING";
  return "AFTER CLASS";
};

const gettingStartedTasks = [
  {
    id: "create-course",
    title: "Create a course",
    description: "Kick off your content pipeline",
    href: "/manage-courses",
  },
  {
    id: "first-topic",
    title: "Create first topic and lesson",
    description: "Help students understand the roadmap",
    href: "/manage-courses",
  },
  {
    id: "connect-calendar",
    title: "Connect Google Calendar",
    description: "Let learners add live classes faster",
    href: "/live-classes",
  },
];

const additionalRecommendedTasks = [
  {
    id: "publish-course",
    title: "Publish your first course",
    description: "Make it visible to students",
    href: "/manage-courses",
  },
  {
    id: "create-quiz",
    title: "Add your first quiz",
    description: "Assess understanding early",
    href: "/manage-courses",
  },
  {
    id: "invite-instructor",
    title: "Invite a co-instructor",
    description: "Share teaching responsibilities",
    href: "/manage-cohorts",
  },
  {
    id: "review-projects",
    title: "Review pending projects",
    description: "Give feedback on submissions",
    href: "/review-projects",
  },
];

const recommendedTasks = [
  ...gettingStartedTasks,
  ...additionalRecommendedTasks,
];

const quickLinks = [
  {
    id: "discord",
    title: "Discord Channel",
    description: "Go to student community",
    href: "https://discord.com",
    external: true,
    icon: DiscordIcon,
  },
  {
    id: "courses",
    title: "My Courses",
    description: "Curate learning content",
    href: "/manage-courses",
    icon: BookOpen,
  },
  {
    id: "classes",
    title: "My Classes",
    description: "Manage class schedules",
    href: "/live-classes",
    icon: CalendarDays,
  },
  {
    id: "track",
    title: "Track Students",
    description: "Track cohort students",
    href: "/track-students",
    icon: ChartLine,
  },
  {
    id: "review",
    title: "Review Projects",
    description: "Assess new submissions",
    href: "/review-projects",
    icon: MessageSquare,
  },
];

const normalizeStudent = (student) => {
  const userId = student?.user_id ?? student?.userId ?? student?.id ?? student?._id ?? null;
  const cohortKey =
    student?.cohort_id ??
    student?.cohortId ??
    student?.cohort ??
    student?.cohort_name ??
    student?.cohortName ??
    null;
  const courseKey =
    student?.course_id ??
    student?.courseId ??
    student?.course ??
    student?.course_name ??
    student?.courseName ??
    null;
  const emailKey = student?.user_email ?? student?.email ?? null;
  const enrolmentKey =
    student?.enrolment_id ??
    student?.enrolmentId ??
    student?.enrollment_id ??
    student?.enrollmentId ??
    null;

  const row_id =
    enrolmentKey != null
      ? String(enrolmentKey)
      : userId != null
        ? `${String(userId)}-${String(cohortKey ?? "cohort")}-${String(courseKey ?? "course")}-${String(emailKey ?? "email")}`
        : makeId();

  return {
    row_id,
    id: userId != null ? String(userId) : makeId(),
    name:
      student.name ||
      [
        student.first_name,
        student.last_name,
        student.user_first_name,
        student.user_last_name,
      ]
        .filter(Boolean)
        .join(" ") ||
      "Student",
    cohort: student.cohort || student.cohort_name || "-",
    course: student.course || student.course_name || "-",
    total: Number(
      student.total ??
        student.total_points ??
        student.total_score ??
        student.score ??
        0
    ),
  };
};

const normalizeProject = (project) => {
  const submission = project.submission || {};
  const projectMeta = submission.project || project.project || {};
  const submittedOnRaw =
    submission.submitted_on ||
    project.submissionDate ||
    project.submitted_on ||
    project.created_at;

  const typeValue =
    projectMeta.type ||
    project.type ||
    submission.type ||
    submission.category ||
    submission.attempt_label;

  const stableSubmissionKey =
    submission.project_id && submission.user_id
      ? `${submission.project_id}-${submission.user_id}-${submittedOnRaw ?? ""}`
      : null;

  return {
    id: stableSubmissionKey || submission.id || project.id || project.submission_id || makeId(),
    title:
      submission.project_title ||
      project.title ||
      projectMeta.title ||
      "Untitled project",
    type: (typeValue || "Submission").toString(),
    course:
      project.course_name ||
      submission.course_name ||
      projectMeta.course_name ||
      "",
    submittedOn: formatRelativeTime(submittedOnRaw),
    status: submission.status || project.status || "PENDING",
    studentName:
      [
        project.user_first_name || submission.user_first_name,
        project.user_last_name || submission.user_last_name,
      ]
        .filter(Boolean)
        .join(" ") || "Student",
  };
};

const normalizeClassSession = (session) => {
  const sessionDate =
    session.sessionDate || session.session_date || session.date;
  const startTime =
    session.sessionStartTime ||
    session.session_start_time ||
    session.start_time;
  const durationMinutes = Number(
    session.durationMinutes ??
      session.session_duration_minutes ??
      session.duration_minutes ??
      session.duration ??
      60
  );
  const startDateTime =
    sessionDate && startTime
      ? new Date(`${sessionDate}T${startTime}`)
      : new Date();
  const endDateTime = new Date(
    startDateTime.getTime() + durationMinutes * 60000
  );
  const joinUrl =
    session.joinUrl ||
    session.join_url ||
    session.live_url ||
    session.session_link ||
    session.link ||
    session.meeting_url ||
    session.session_meeting_link ||
    "";

  return {
    id: session.id || session.session_id || makeId(),
    title: session.title || session.class_title || "Untitled session",
    sessionType: session.sessionType || session.class_type || "CLASS",
    status: session.status || "UPCOMING",
    location: session.location || session.class_location || "Online",
    facilitator: session.owner_first_name
      ? `${session.owner_first_name} ${session.owner_last_name || ""}`.trim()
      : session.facilitator || "",
    startDateTime,
    endDateTime,
    joinUrl,
  };
};

const normalizeAttendanceAlert = (item) => {
  const totals = parseAttendanceTotals(item.present_total);
  const percent = totals.total
    ? Math.round((totals.present / totals.total) * 100)
    : 0;
  const dateRaw = getSessionDateValue(item);
  return {
    id: item.session_id || item.id || makeId(),
    sessionId: item.session_id || item.sessionId || item.id,
    sessionTitle:
      item.sessionTitle || item.classTitle || item.session_title || "Untitled",
    sessionType: (
      item.sessionType ||
      item.classType ||
      item.session_type ||
      "CLASS"
    ).toUpperCase(),
    dateLabel: item.dateLabel || formatSessionDateLabel(item) || "",
    status: percent >= 100 ? "COMPLETED" : getAttendanceStatus(item),
    percent,
    dateRaw,
  };
};

const getDayBucket = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const dayAfter = new Date(today);
  dayAfter.setDate(today.getDate() + 2);

  if (date >= today && date < tomorrow) return "today";
  if (date >= tomorrow && date < dayAfter) return "tomorrow";
  return "afterwards";
};

const formatDate = (date) =>
  date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

export default function DashboardPage() {
  const sidebarTitle = useSidebarPageTitle();
  const { user } = useUser();
  const { getToken, isLoaded } = useAuth();
  const queryClient = useQueryClient();
  const [students, setStudents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [classSessions, setClassSessions] = useState([]);
  const [attendanceAlerts, setAttendanceAlerts] = useState([]);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [pendingAttendanceFilter, setPendingAttendanceFilter] =
    useState("today");
  const [selectedAttendanceSession, setSelectedAttendanceSession] =
    useState(null);
  const [attendanceDialogAttendees, setAttendanceDialogAttendees] = useState(
    []
  );
  const [attendanceDialogLoading, setAttendanceDialogLoading] = useState(false);
  const [cohortOptions, setCohortOptions] = useState([]);
  const [pathwayOptions, setPathwayOptions] = useState([]);
  const [taskList, setTaskList] = useState(
    recommendedTasks.map((task) => ({ ...task, completed: false }))
  );
  const storageKey = `getting-started-${user?.id || "anon"}`;
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setTaskList(parsed);
        }
      }
    } catch (error) {
      console.error("Unable to load saved tasks", error);
    }
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(taskList));
    } catch (error) {
      console.error("Unable to persist tasks", error);
    }
  }, [taskList, storageKey]);
  const loadAttendanceAlerts = useCallback(async () => {
    if (!api_endpoint || !isLoaded) return [];
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["liveclass", "tm-owner", "past-sessions"],
        queryFn: ({ signal }) =>
          apiRequest(getToken, {
            path: "/liveclass/tm-owner/past-sessions",
            auth: "required",
            signal,
          }),
      });

      const payload = data?.past_sessions_attendance_counts || [];
      return payload
        .map(normalizeAttendanceAlert)
        .filter((alert) => alert.percent < 100)
        .slice(0, 4);
    } catch (error) {
      console.error("Unable to fetch attendance alerts", error);
      return [];
    }
  }, [api_endpoint, getToken, isLoaded, queryClient]);

  const fetchUnreviewedProjects = useCallback(async () => {
    if (!api_endpoint || !isLoaded || !user?.id) return [];

    try {
      const items = await queryClient.fetchQuery({
        queryKey: ["project", "ungraded", String(user.id)],
        queryFn: ({ signal }) =>
          apiRequest(getToken, {
            path: `/project/ungraded/${user.id}`,
            auth: "required",
            signal,
          }).then((d) => (Array.isArray(d?.projects) ? d.projects : [])),
      });

      const fetchProjectDetails = async (projectId) => {
        if (!projectId) return null;
        try {
          const project = await queryClient.fetchQuery({
            queryKey: ["project", "get", String(projectId)],
            queryFn: ({ signal }) =>
              apiRequest(getToken, {
                path: `/project/get/${projectId}`,
                auth: "required",
                signal,
              }).then((d) => d?.project || null),
          });
          return project || null;
        } catch (error) {
          console.error("Unable to fetch project details", error);
          return null;
        }
      };

      const withDetails = await Promise.all(
        (items || []).slice(0, 4).map(async (item) => {
          const submission = item?.submission
            ? { ...item.submission }
            : {
                ...item,
              };

          const projectId =
            submission?.project_id ||
            item?.project_id ||
            item?.submission?.project_id;
          const details = await fetchProjectDetails(projectId);
          if (details) {
            submission.project = details;
          }

          return {
            ...item,
            submission,
            course_name:
              item.course_name || submission.course_name || item.course || "",
            course_slug:
              item.course_slug || submission.course_slug || item.course_slug || "",
            user_first_name:
              item.user_first_name || submission.user_first_name || "",
            user_last_name:
              item.user_last_name || submission.user_last_name || "",
            user_email: item.user_email || submission.user_email || "",
          };
        })
      );

      return withDetails.slice(0, 4);
    } catch (error) {
      console.error("Unable to fetch project submissions", error);
      return [];
    }
  }, [api_endpoint, getToken, isLoaded, queryClient, user?.id]);

  const refetchReviewedProjects = useCallback(async () => {
    const refreshed = await fetchUnreviewedProjects();
    setProjects(refreshed.slice(0, 4));
  }, [fetchUnreviewedProjects]);

  const refreshAttendanceAlerts = useCallback(async () => {
    const alerts = await loadAttendanceAlerts();
    setAttendanceAlerts(alerts);
  }, [loadAttendanceAlerts]);
  const [attendanceFilter, setAttendanceFilter] = useState("today");
  const [loading, setLoading] = useState(false);
  const [myCourses, setMyCourses] = useState([]);
  const [studentPage, setStudentPage] = useState(1);
  const [studentRowsPerPage, setStudentRowsPerPage] = useState(10);

  useEffect(() => {
    if (!api_endpoint || !isLoaded) return;

    let ignore = false;
    const hydrate = async () => {
      try {
        setLoading(true);
        const fetchStudents = async () => {
          try {
            const cohortsData = await queryClient.fetchQuery({
              queryKey: ["cohort", "tm"],
              queryFn: ({ signal }) =>
                apiRequest(getToken, {
                  path: "/cohort/tm",
                  auth: "required",
                  signal,
                }),
            });
            const cohorts = cohortsData?.cohorts || [];
            setCohortOptions(
              cohorts.map((c) => ({
                value: c.id,
                label: c.name || c.cohort_name || `Cohort ${c.id}`,
              }))
            );
            if (!cohorts.length) {
              return [];
            }

            const enrolmentResponses = await Promise.allSettled(
              cohorts.map((cohort) =>
                queryClient.fetchQuery({
                  queryKey: ["cohort", cohort.id, "enrolments", "skip", 200],
                  queryFn: async () => {
                    const token = await getToken();
                    if (!token) return { enrolments: [] };
                    const reqHeaders = {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`,
                    };
                    const enrolments = await fetchAllPaginatedItems({
                      url: api_endpoint + `/cohort/enrolments/${cohort.id}`,
                      token,
                      headers: reqHeaders,
                      mode: "skip",
                      pageSize: 200,
                      maxPages: 200,
                      dedupe: false,
                      selectItems: (data) => data?.enrolments ?? [],
                    });
                    return { enrolments };
                  },
                })
              )
            );
            const enrolments = enrolmentResponses.flatMap((result) =>
              result.status === "fulfilled"
                ? result.value?.enrolments || []
                : []
            );

            return enrolments.map(normalizeStudent);
          } catch (error) {
            console.error("Unable to fetch cohort enrolments", error);
            setCohortOptions([]);
            return [];
          }
        };

        const fetchPathways = async () => {
          try {
            const res = await queryClient.fetchQuery({
              queryKey: ["pathway", "all"],
              queryFn: ({ signal }) =>
                apiRequest(getToken, {
                  path: "/pathway/",
                  auth: "required",
                  signal,
                }),
            });
            const items =
              res?.pathways || res?.data || res || [];
            setPathwayOptions(
              (Array.isArray(items) ? items : []).map((p) => ({
                value: p.id || p.pathway_id,
                label:
                  p.title ||
                  p.name ||
                  p.pathway_title ||
                  `Pathway ${p.id || p.pathway_id}`,
              }))
            );
          } catch (error) {
            console.error("Unable to fetch pathways", error);
            setPathwayOptions([]);
          }
        };


        const fetchUpcomingClasses = async () => {
          try {
            const response = await queryClient.fetchQuery({
              queryKey: ["liveclass", "upcoming-sessions"],
              queryFn: ({ signal }) =>
                apiRequest(getToken, {
                  path: "/liveclass/upcoming-sessions",
                  auth: "required",
                  signal,
                }),
            });
            const payload =
              response?.items ||
              response?.sessions ||
              response ||
              [];
            return payload.map(normalizeClassSession);
          } catch (error) {
            console.error("Unable to fetch upcoming classes", error);
            return [];
          }
        };

        const fetchAttendanceAlertsScoped = async () => loadAttendanceAlerts();

        const fetchMyCourses = async () => {
          try {
            const res = await queryClient.fetchQuery({
              queryKey: queryKeys.courses.my(),
              queryFn: async ({ signal }) => {
                const raw = await apiRequest(getToken, {
                  path: "/course/tm-courses",
                  auth: "required",
                  signal,
                });
                const payload =
                  raw?.courses ||
                  raw?.data ||
                  raw?.items ||
                  raw ||
                  [];
                return Array.isArray(payload) ? payload : [];
              },
            });
            return Array.isArray(res) ? res : [];
          } catch (error) {
            console.error("Unable to fetch courses", error);
            return [];
          }
        };

        const [
          studentItems,
          projectItems,
          classItems,
          attendanceItems,
          courseItems,
        ] = await Promise.all([
          fetchStudents(),
          fetchUnreviewedProjects(),
          fetchUpcomingClasses(),
          fetchAttendanceAlertsScoped(),
          fetchMyCourses(),
        ]);
        await fetchPathways();

        if (!ignore) {
          setStudents(studentItems);
          setProjects(projectItems.slice(0, 4));
          setClassSessions(classItems);
          setAttendanceAlerts(attendanceItems);
          setMyCourses(courseItems);
        }
      } catch (error) {
        console.error("Unable to load dashboard data", error);
        if (!ignore) {
          setStudents([]);
          setProjects([]);
          setClassSessions([]);
          setAttendanceAlerts([]);
          setMyCourses([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    hydrate();
    return () => {
      ignore = true;
    };
  }, [api_endpoint, getToken, isLoaded, loadAttendanceAlerts, fetchUnreviewedProjects, queryClient, user?.id]);

  const atRiskStudents = useMemo(() => {
    if (!students.length) return [];
    const sorted = [...students].sort((a, b) => a.total - b.total);
    const takeCount = Math.max(1, Math.round(sorted.length * 0.25));
    return sorted.slice(0, takeCount);
  }, [students]);

  const metrics = useMemo(() => {
    const cohortCount = new Set(students.map((student) => student.cohort)).size;
    const courseCount = new Set(students.map((student) => student.course)).size;
    const courseList = Array.isArray(myCourses) ? myCourses : [];
    const totalCourseEnrollments = courseList.reduce(
      (sum, c) => sum + (Number(c.total_students) || 0),
      0
    );
    return {
      activeCohortEnrollments: students.length,
      cohortCount,
      totalCourseEnrollments,
      courseCount,
    };
  }, [students, myCourses]);

  const filteredAttendance = useMemo(() => {
    return attendanceAlerts.filter((alert) => {
      const dateValue = alert.dateRaw ? new Date(alert.dateRaw) : null;
      if (!dateValue || Number.isNaN(dateValue.getTime()))
        return attendanceFilter === "afterwards";
      return getDayBucket(dateValue) === attendanceFilter;
    });
  }, [attendanceAlerts, attendanceFilter]);

  const upcomingClassesForSection = useMemo(
    () =>
      classSessions.map((session) => {
        const startDateTime =
          session.startDateTime instanceof Date &&
          !Number.isNaN(session.startDateTime.getTime())
            ? session.startDateTime
            : null;

        const duration = Math.max(
          15,
          Math.round(
            ((session.endDateTime?.getTime?.() || 0) -
              (session.startDateTime?.getTime?.() || 0)) /
              60000
          ) ||
            Number(session.durationMinutes) ||
            60
        );

        const endDateTime = startDateTime
          ? add(startDateTime, { minutes: duration })
          : null;

        return {
          id: session.id,
          class_title: session.title,
          class_location: session.location,
          session_start_time: startDateTime
            ? format(startDateTime, "HH:mm")
            : "00:00",
          session_end_time: endDateTime ? format(endDateTime, "HH:mm") : "",
          session_date: startDateTime
            ? format(startDateTime, "yyyy-MM-dd")
            : "",
          session_duration_minutes: duration,
          startDateTime,
          endDateTime,
          session_type: session.sessionType || "CLASS",
          join_url: session.joinUrl,
          facilitator: session.facilitator,
        };
      }),
    [classSessions]
  );

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(atRiskStudents.length / studentRowsPerPage) || 1
    );
    if (studentPage > totalPages) {
      setStudentPage(totalPages);
    }
  }, [atRiskStudents.length, studentPage, studentRowsPerPage]);

  const fetchSessionAttendees = useCallback(
    async (sessionId) => {
      if (!sessionId || !api_endpoint) return;
      try {
        setAttendanceDialogLoading(true);
        const data = await queryClient.fetchQuery({
          queryKey: ["liveclass", "session-attendees", String(sessionId)],
          queryFn: ({ signal }) =>
            apiRequest(getToken, {
              path: `/liveclass/session-attendees/${sessionId}`,
              auth: "required",
              signal,
            }),
        });
        setAttendanceDialogAttendees(data?.attendees || []);
      } catch (error) {
        console.error("Unable to fetch attendees", error);
        setAttendanceDialogAttendees([]);
      } finally {
        setAttendanceDialogLoading(false);
      }
    },
    [api_endpoint, getToken, queryClient]
  );

  useEffect(() => {
    if (!attendanceDialogOpen || !selectedAttendanceSession) return;
    const sessionId =
      selectedAttendanceSession.sessionId || selectedAttendanceSession.id;
    fetchSessionAttendees(sessionId);
  }, [attendanceDialogOpen, selectedAttendanceSession, fetchSessionAttendees]);

  const handleAttendanceDialogOpenChange = (isOpen) => {
    setAttendanceDialogOpen(isOpen);
    if (!isOpen) {
      setSelectedAttendanceSession(null);
      setAttendanceDialogAttendees([]);
    }
  };

  const openAttendanceDialog = (alert) => {
    setSelectedAttendanceSession(alert);
    setAttendanceDialogAttendees([]);
    setAttendanceDialogOpen(true);
  };

  const studentColumns = useMemo(
    () => [
      {
        key: "cohort",
        header: "Cohort",
        render: (student) => (
          <Badge variant="outline" className="">
            {student.cohort}
          </Badge>
        ),
      },
      {
        key: "name",
        header: "Name",
        render: (student) => (
          <span className="font-heading text-(--neutral-900)">
            {student.name}
          </span>
        ),
      },
      {
        key: "course",
        header: "Course",
        render: (student) => (
          <span className="text-sm font-body text-(--neutral-800)">
            {student.course}
          </span>
        ),
      },
      {
        key: "total",
        header: "Total",
        render: (student) => (
          <span className="font-semibold text-(--neutral-900)">
            {student.total.toLocaleString()}
          </span>
        ),
      },
      // {
      //   key: "action",
      //   header: "",
      //   render: () => (
      //     <Button variant="ghost" size="icon">
      //       <MoreVertical className="w-4 h-4" />
      //     </Button>
      //   ),
      // },
    ],
    []
  );

  const studentPageData = useMemo(() => {
    const start = (studentPage - 1) * studentRowsPerPage;
    const end = start + studentRowsPerPage;
    return atRiskStudents.slice(start, end);
  }, [atRiskStudents, studentPage, studentRowsPerPage]);

  const totalStudentPages = Math.max(
    1,
    Math.ceil(atRiskStudents.length / studentRowsPerPage) || 1
  );

  const toggleTask = (taskId) =>
    setTaskList((items) =>
      items.map((item) =>
        item.id === taskId ? { ...item, completed: !item.completed } : item
      )
    );

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Team";

  return (
    <>
      <div className="space-y-6 sm:px-0 overflow-x-hidden">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)] min-w-0">
          <div className="space-y-6 min-w-0">
            <div className="flex flex-col gap-4 mt-4 pb-2 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
              <div className="space-y-2 mt-2 lg:flex-1">
                <h1 className="font-heading text-(--neutral-900)">
                   Welcome, <span className="text-(--color-primary)">{displayName}</span>
                </h1>
                <p className="font-body text-(--neutral-700)">
                  Track students and curate content or live classes.
                </p>
                <MakeAnnouncementDialog
                  mode="group"
                  cohortOptions={cohortOptions}
                  pathwayOptions={pathwayOptions}
                  triggerLabel="Make Announcement"
                  triggerVariant="outline"
                  triggerClassName="text-(--color-primary) h-auto font-semibold px-0"
                  showIcon
                />
              </div>
              <div className="grid w-full gap-2 lg:gap-1 grid-flow-col auto-cols-[150px] overflow-x-auto sm:grid-flow-row sm:grid-cols-2 sm:auto-cols-auto sm:overflow-visible lg:grid-cols-2 lg:auto-rows-fr lg:w-70 xl:w-[320px] lg:shrink-0">
                <DashboardStatCard
                  variant="progress"
                  title="Active Cohort Enrolments"
                  value={metrics.activeCohortEnrollments}
                  subtitle={`Across ${metrics.cohortCount} cohorts`}
                  progressPercent={Math.min(
                    100,
                    metrics.activeCohortEnrollments
                  )}
                />
                <DashboardStatCard
                  variant="progress"
                  title="Total Course Enrolments"
                  value={metrics.totalCourseEnrollments}
                  subtitle={`Across ${metrics.courseCount} courses`}
                  progressPercent={Math.min(
                    100,
                    metrics.totalCourseEnrollments
                  )}
                />
              </div>
            </div>

            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-(--neutral-900)">
                    Your Cohort Students
                  </h2>
                  <p className="text-(--neutral-700)">
                    Below are students that require more attention
                  </p>
                </div>
                <Button asChild className="w-full sm:w-auto font-bold hover:text-(--color-accent)" variant="link">
                  <Link to="/manage-cohorts">
                    View All Students
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>

              <div className="overflow-x-auto">
                <DefaultTable
                  columns={studentColumns}
                  data={studentPageData}
                  emptyMessage={
                    loading ? <Spinner /> : "No students flagged right now."
                  }
                />
              </div>
              <Pagination
                currentPage={studentPage}
                totalPages={totalStudentPages}
                rowsPerPage={studentRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={(value) => {
                  setStudentRowsPerPage(value);
                  setStudentPage(1);
                }}
                onPageChange={(page) =>
                  setStudentPage(Math.max(1, Math.min(page, totalStudentPages)))
                }
                totalRows={atRiskStudents.length}
                rowLabel="row items"
                showSelectedCount={false}
              />
            </section>

            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-heading text-(--neutral-900)">
                    New Project Submissions
                  </h2>
                  <p className="text-(--neutral-700)">
                    Review projects submitted by your cohort students
                  </p>
                </div>
                <Button asChild className="w-full sm:w-auto font-bold hover:text-(--color-accent)" variant="link">
                  <Link to="/review-projects">
                    View All Submissions
                    <ChevronRight className="w-5 h-5" />
                  </Link>
                </Button>
              </div>

              <div className="space-y-4">
                {projects.slice(0, 4).map((project) => {
                  const submission = project?.submission;
                  if (!submission) return null;

                  const key = `${submission?.project_id ?? "p"}-${submission?.user_id ?? "u"}-${submission?.submitted_on ?? submission?.last_updated_at ?? "t"}`;

                  return (
                    <UnreviewedCard
                      key={key}
                      submission={submission}
                      courseName={project.course_name || ""}
                      courseSlug={project.course_slug || ""}
                      userFirstName={project.user_first_name || ""}
                      userLastName={project.user_last_name || ""}
                      userEmail={project.user_email || ""}
                      viewerIsReviewer
                      refetchReviewedProjects={refetchReviewedProjects}
                    />
                  );
                })}
                {!projects.length && (
                  <>
                    {loading ? (
                      <Spinner />
                    ) : (
                      <EmptyState
                        description="You're all caught up for now. Check back later for new submissions to review."
                        icon={BookOpenIcon}
                        className="bg-(--neutral-100) rounded-lg"
                      />
                    )}
                  </>
                )}
              </div>
            </section>
          </div>

          <div className="space-y-6">
            {false && (
              <Card className="border-(--neutral-300)">
                <CardHeader>
                  <CardTitle className="text-xl font-heading">
                    Getting Started
                  </CardTitle>
                  <CardDescription className="text-(--neutral-500)">
                    Unlock that Aha! moment by walking through these guided
                    steps
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {taskList
                      .slice(0, gettingStartedTasks.length)
                      .map((task) => (
                        <div
                          key={task.id}
                          className="flex items-center gap-3 rounded-md border border-(--neutral-300) bg-(--neutral-50) px-3 py-3"
                        >
                          <Checkbox
                            checked={task.completed}
                            onCheckedChange={() => toggleTask(task.id)}
                            className="mt-0"
                          />
                          <div className="flex-1 leading-tight">
                            <p className="font-heading text-(--neutral-900)">
                              {task.title}
                            </p>
                            {/* <p className="text-sm text-(--neutral-500)">{task.description}</p> */}
                          </div>
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={task.href}>Start</Link>
                          </Button>
                        </div>
                      ))}
                  </div>
                  <div className="flex justify-center">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="link" className="font-semibold">
                          More Recommended Tasks
                          <ArrowUpRight className="ml-1 w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle>More Recommended Tasks</DialogTitle>
                          <DialogDescription>
                            Quick wins to finish setting up your teaching
                            workspace.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                          {taskList.map((task) => (
                            <div
                              key={`extra-${task.id}`}
                              className="flex items-center gap-3 rounded-md border border-(--neutral-200) bg-(--neutral-50) px-3 py-2"
                            >
                              <Checkbox
                                checked={task.completed}
                                onCheckedChange={() => toggleTask(task.id)}
                                className="mt-0"
                              />
                              <div className="flex-1 leading-tight">
                                <p className="font-heading text-(--neutral-900)">
                                  {task.title}
                                </p>
                                <p className="text-(--neutral-800)">
                                  {task.description}
                                </p>
                              </div>
                              <Button variant="ghost" size="sm" asChild>
                                <Link to={task.href}>Start</Link>
                              </Button>
                            </div>
                          ))}
                        </div>
                        <DialogFooter>
                          <Button variant="" asChild className="w-full">
                            <Link to="/manage-courses">Go to Courses</Link>
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            )}

            <UpcomingClassesSection
              isLoading={loading}
              upcomingClasses={upcomingClassesForSection}
              add={add}
              format={format}
            />

            <PendingAttendanceLogsSection
              isLoading={loading}
              pendingLogs={attendanceAlerts}
              onReview={openAttendanceDialog}
              onViewAll={() => {}}
            />

            <div className="max-md:mt-3 border border-(--neutral-300) rounded-lg bg-(--neutral-50)">
              <div className="p-4">
                <h3 className="mb-1">Quick Links</h3>
                <p className="text-(--neutral-800)">
                  Click any item below to be directed there
                </p>
              </div>
              <div className="flex flex-col">
                {quickLinks.map((link, idx) => {
                  const Icon = link.icon;
                  const Wrapper = link.external ? "a" : Link;
                  const wrapperProps = link.external
                    ? { href: link.href, target: "_blank", rel: "noreferrer" }
                    : { to: link.href };
                  return (
                    <Wrapper
                      key={link.id}
                      {...wrapperProps}
                      className={`flex items-start gap-3 p-4 border border-(--neutral-300) transition group hover:bg-(--neutral-100) ${
                        idx === 0 ? "bg-(--neutral-100)" : "bg-(--neutral-50)"
                      }`}
                    >
                      <Icon className="w-6 h-6 mt-1 text-(--color-primary) group-hover:text-(--color-accent) transition-colors" />
                      <div>
                        <p className="text-md font-heading text-(--neutral-900) leading-tight">
                          {link.title}
                        </p>
                        <p className="text-sm text-(--neutral-700) leading-tight">
                          {link.description}
                        </p>
                      </div>
                    </Wrapper>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <MarkAttendanceDialog
        open={attendanceDialogOpen}
        onOpenChange={handleAttendanceDialogOpenChange}
        sessionTitle={selectedAttendanceSession?.sessionTitle || ""}
        sessionDate={selectedAttendanceSession?.dateLabel || ""}
        attendees={attendanceDialogAttendees}
        loading={attendanceDialogLoading}
        sessionId={
          selectedAttendanceSession?.sessionId || selectedAttendanceSession?.id
        }
        onAttendanceSaved={refreshAttendanceAlerts}
      />
    </>
  );
}
